import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import _classNames from 'classnames';
import _get from 'lodash/get';
import { IconButton, Typography, makeStyles } from '@material-ui/core';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import PageIntroBlock from 'components/PageIntroBlock';
import VisibilitySensor from 'react-visibility-sensor';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { SeriesResourceDetails } from '../SeriesResourceDetails';

const useStyles = makeStyles({
  root: {
    padding: props => props.isMobile && '20px 20px 0px',
  },
  hiddenSection: {
    visibility: 'hidden',
  },
  openLink: {
    display: 'inline-flex',
    marginTop: 2,

    '& svg': {
      fontSize: props => props.fontSize,
    },
  },
  richText: {
    '& p, & span[role="button"]': {
      fontSize: props => props.fontSize,
    },
  },
  iconButton: {
    alignSelf: 'flex-start',
    padding: 6,
    marginLeft: 8,
  },
  articleLinks: {
    padding: '0px !important',
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: props => (props.isMobile ? 'space-between' : 'flex-end'),
    marginTop: 10,

    '& button': {
      marginLeft: 20,
    },
  },
});

export const SeriesArticlePreview = ({
  isMobile,
  title,
  description,
  resource,
  seriesSiteCopy,
  allowSignIn,
  type,
  fontSize,
  completeResource,
  enableScrollCompletion,
  resourcePathname,
  actionsNode,
  showShareAction,
  renderHeartButton,
  showOpenInNewIcon = true,
}) => {
  const classes = useStyles({ fontSize, isMobile });
  const ref = useRef(null);
  const [visibilitySensorStatus, setVisibilitySensorStatus] = useState(null);
  const slug = _get(resource, 'fields.slug');

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    if (!enableScrollCompletion) {
      setVisibilitySensorStatus(false);
    } else {
      const rect = ref.current.getBoundingClientRect();
      const isFullyVisible = rect.bottom <= window.innerHeight;
      setVisibilitySensorStatus(!isFullyVisible);
    }
  }, [enableScrollCompletion]);

  const onVisibilityChange = isVisible => {
    if (isVisible) {
      completeResource(resource, true);
      setVisibilitySensorStatus(false);
    }
  };

  return (
    <div className={_classNames(classes.root, 'article')}>
      <Typography color="textPrimary" variant={isMobile ? 'h6' : 'h5'}>
        {title}
        {showOpenInNewIcon && (
          <IconButton className={classes.iconButton} aria-label="Open">
            <Link
              to={`/articles/${_get(resource, 'fields.slug')}`}
              target="_blank"
              className={classes.openLink}
            >
              <OpenInNewIcon className={classes.openIcon} />
            </Link>
          </IconButton>
        )}
      </Typography>
      <SeriesResourceDetails
        resource={resource}
        resourcePathname={resourcePathname}
        seriesSiteCopy={seriesSiteCopy}
        allowSignIn={allowSignIn}
        type={type}
        fontSize={fontSize}
        isMobile={isMobile}
        showShareAction={showShareAction}
        renderHeartButton={renderHeartButton}
      />
      <PageIntroBlock type="reference" slug={slug} page="activity" />
      {typeof description === 'object' ? (
        <RichTextReactRenderer
          className={classes.richText}
          data={description}
          linkAsPlainText
        />
      ) : (
        <Typography>{description}</Typography>
      )}
      <VisibilitySensor
        active={!!visibilitySensorStatus}
        scrollCheck
        onChange={onVisibilityChange}
      >
        <div ref={ref} className={classes.actionButtons}>
          {actionsNode}
        </div>
      </VisibilitySensor>
    </div>
  );
};
